'use client';

import React, { useState, useEffect } from 'react';
import { useAccessCode } from '@/contexts/AccessCodeContext';
import { usePathname, useRouter } from 'next/navigation';


// ── Token Theme Definitions ──
const TOKEN_THEMES = [
  { emoji: '⭐', label: 'Gold Stars',     filledBg: '#b8922a', filledBorder: '#b8922a', glow: 'rgba(184,146,42,0.5)' },
  { emoji: '🍷', label: 'Wine Glass',     filledBg: '#7b2d42', filledBorder: '#7b2d42', glow: 'rgba(123,45,66,0.45)' },
  { emoji: '🍾', label: 'Wine Bottle',    filledBg: '#2d5a27', filledBorder: '#2d5a27', glow: 'rgba(45,90,39,0.45)' },
  { emoji: '💰', label: 'Money Bag',      filledBg: '#4a7c3f', filledBorder: '#4a7c3f', glow: 'rgba(74,124,63,0.45)' },
  { emoji: '💵', label: 'Cash',           filledBg: '#3a7d44', filledBorder: '#3a7d44', glow: 'rgba(58,125,68,0.45)' },
  { emoji: '💎', label: 'Diamonds',       filledBg: '#1a6b8a', filledBorder: '#1a6b8a', glow: 'rgba(26,107,138,0.45)' },
  { emoji: '🏆', label: 'Trophy',         filledBg: '#c0892a', filledBorder: '#c0892a', glow: 'rgba(192,137,42,0.5)' },
  { emoji: '🎯', label: 'Bullseye',       filledBg: '#b03030', filledBorder: '#b03030', glow: 'rgba(176,48,48,0.45)' },
  { emoji: '👠', label: 'High Heels',     filledBg: '#a0306a', filledBorder: '#a0306a', glow: 'rgba(160,48,106,0.45)' },
];

export default function AccessCodeGate({ children }: { children: React.ReactNode }) {
  const { isUnlocked, isLoading, isAccessExpired, validateCode } = useAccessCode();
  const pathname = usePathname();
  const router = useRouter();
  const [tokens, setTokens] = useState<boolean[]>([false, false, false, false, false, false]);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Admin modal state
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [adminError, setAdminError] = useState('');
  const [adminSubmitting, setAdminSubmitting] = useState(false);

  // Clear input field every time the session is locked
  useEffect(() => {
    if (!isUnlocked) {
      setCode('');
      setTokens([false, false, false, false, false, false]);
      setError('');
    }
  }, [isUnlocked]);

  // Pick a random theme once per session (stable across re-renders)
  const [theme, setTheme] = useState(TOKEN_THEMES[0]);
  useEffect(() => {
    setTheme(TOKEN_THEMES[Math.floor(Math.random() * TOKEN_THEMES.length)]);
  }, []);

  const handleAdminLinkClick = () => {
    setAdminPassword('');
    setAdminError('');
    setShowAdminModal(true);
  };

  const handleAdminModalClose = () => {
    setShowAdminModal(false);
    setAdminPassword('');
    setAdminError('');
  };

  const handleAdminSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!adminPassword.trim()) {
      setAdminError('Please enter the admin password.');
      return;
    }
    setAdminSubmitting(true);
    setAdminError('');
    try {
      const res = await fetch('/api/admin-auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: adminPassword }),
      });
      const data = await res.json();
      if (data.success) {
        setShowAdminModal(false);
        window.location.href = '/admin-testing';
      } else {
        setAdminError(data.error || 'Incorrect password. Access denied.');
        setAdminPassword('');
      }
    } catch {
      setAdminError('An error occurred. Please try again.');
    } finally {
      setAdminSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#1e3a5f' }}>
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 rounded-full border-2 border-white/20 border-t-white animate-spin" />
          <span className="text-sm font-medium text-white/60" style={{ fontFamily: 'Sora, sans-serif' }}>Loading…</span>
        </div>
      </div>
    );
  }

  // Allow admin routes to bypass the access code gate entirely
  if (pathname === '/admin-testing' || pathname === '/admin') {
    return <>{children}</>;
  }

  // Lockout screen
  if (isUnlocked && isAccessExpired) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4" style={{ background: '#1e3a5f' }}>
        <div
          className="w-full max-w-sm rounded-xl p-8 text-center"
          style={{
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 24px 64px rgba(0,0,0,0.35), 0 8px 24px rgba(0,0,0,0.2)',
          }}
        >
          {/* Wordmark */}
          <div className="mb-6">
            <h1 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 700, fontSize: '1.5rem', color: '#1e3a5f', lineHeight: 1.2 }}>
              OTB <span style={{ color: '#d4a832' }}>Clinical Coach</span>
            </h1>
            <p style={{ fontFamily: 'Sora, sans-serif', fontSize: '0.6rem', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#9ca3af', marginTop: '4px' }}>
              ABA Edition · Clinician Access
            </p>
          </div>

          <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#fee2e2', border: '1.5px solid #fca5a5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <svg style={{ width: '20px', height: '20px', color: '#dc2626' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>

          <h2 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 700, fontSize: '1.125rem', color: '#111827', marginBottom: '8px' }}>Access Limit Reached</h2>
          <p style={{ fontFamily: 'Sora, sans-serif', fontSize: '0.8125rem', color: '#6b7280', lineHeight: 1.6, marginBottom: '16px' }}>
            Your access code has either expired (90 days from first use) or reached its generation limit.
          </p>
          <div style={{ background: '#f2f3f5', border: '1px solid #ced2d8', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
            <p style={{ fontFamily: 'Sora, sans-serif', fontSize: '0.8125rem', color: '#374151' }}>
              Contact <strong style={{ color: '#1e3a5f' }}>OTB Learning Group</strong> to purchase a new access code.
            </p>
          </div>
          <p style={{ fontFamily: 'Sora, sans-serif', fontSize: '0.6875rem', color: '#9ca3af' }}>
            BCBAs · BCaBAs · RBT Supervisors · Student Analysts
          </p>
        </div>
      </div>
    );
  }

  if (isUnlocked) {
    return <>{children}</>;
  }

  // ── Token Board keypad logic ──
  const filledCount = tokens.filter(Boolean).length;

  const handleKeyPress = async (key: string) => {
    if (isSubmitting) return;

    if (key === 'DEL') {
      if (code.length === 0) return;
      const newCode = code.slice(0, -1);
      setCode(newCode);
      setError('');
      // Unfill last token
      const newTokens = [...tokens];
      const lastFilled = newTokens.lastIndexOf(true);
      if (lastFilled !== -1) newTokens[lastFilled] = false;
      setTokens(newTokens);
      return;
    }

    if (key === 'GO') {
      if (filledCount < 6) return;
      setError('');
      setIsSubmitting(true);
      const result = await validateCode(code);
      if (!result.success) {
        setError(result.error || 'Invalid access code.');
        setTokens([false, false, false, false, false, false]);
        setCode('');
      } else {
        router.push('/select-pathway');
      }
      setIsSubmitting(false);
      return;
    }

    // Digit key
    if (code.length >= 6) return;
    const newCode = code + key;
    setCode(newCode);
    setError('');

    // Fill next token
    const newTokens = [...tokens];
    const nextEmpty = newTokens.indexOf(false);
    if (nextEmpty !== -1) newTokens[nextEmpty] = true;
    setTokens(newTokens);

    // Auto-submit when 6 tokens filled
    if (newCode.length === 6) {
      setIsSubmitting(true);
      const result = await validateCode(newCode);
      if (!result.success) {
        setError(result.error || 'Invalid access code.');
        setTokens([false, false, false, false, false, false]);
        setCode('');
      } else {
        router.push('/select-pathway');
      }
      setIsSubmitting(false);
    }
  };

  const keypadRows = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['DEL', '0', 'GO'],
  ];

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-8"
      style={{ background: '#1e3a5f' }}
    >
      {/* White Card */}
      <div
        className="w-full max-w-sm animate-fade-in-up"
        style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.35), 0 8px 24px rgba(0,0,0,0.2)',
          padding: '32px 28px',
        }}
      >
        {/* ── Wordmark ── */}
        <div className="text-center mb-6">
          <h1 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 700, fontSize: '1.625rem', color: '#1e3a5f', lineHeight: 1.2, marginBottom: '4px' }}>
            OTB <span style={{ color: '#d4a832' }}>Clinical Coach</span>
          </h1>
          <p style={{ fontFamily: 'Sora, sans-serif', fontSize: '0.6rem', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#9ca3af' }}>
            ABA Edition · Clinician Access
          </p>
        </div>

        {/* ── Token Board Label ── */}
        <p style={{ fontFamily: 'Playfair Display, serif', fontWeight: 600, fontSize: '1rem', color: '#1e3a5f', marginBottom: '10px' }}>
          Token Board
        </p>

        {/* ── Token Strip ── */}
        <div
          style={{
            background: '#f2f3f5',
            border: '2px solid #ced2d8',
            borderRadius: '8px',
            padding: '10px 12px',
            display: 'flex',
            gap: '8px',
            justifyContent: 'center',
            marginBottom: '16px',
          }}
        >
          {tokens.map((filled, i) => (
            <div
              key={i}
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                border: filled ? `2px solid ${theme.filledBorder}` : '2px solid #ced2d8',
                background: filled ? theme.filledBg : '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                boxShadow: filled ? `0 0 12px ${theme.glow}, 0 0 4px ${theme.glow}` : 'none',
                animation: filled ? 'tokenFill 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) forwards' : 'none',
              }}
            >
              {filled && (
                <span style={{ fontSize: '1.1rem', lineHeight: 1 }}>{theme.emoji}</span>
              )}
            </div>
          ))}
        </div>

        {/* ── First / Then Strip ── */}
        <div style={{ display: 'flex', alignItems: 'stretch', gap: '8px', marginBottom: '16px' }}>
          {/* FIRST box */}
          <div style={{
            flex: 1,
            border: '1.5px solid #ced2d8',
            borderRadius: '8px',
            padding: '8px 10px',
            background: '#ffffff',
          }}>
            <p style={{ fontFamily: 'Sora, sans-serif', fontSize: '0.55rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#9ca3af', marginBottom: '2px' }}>First</p>
            <p style={{ fontFamily: 'Sora, sans-serif', fontSize: '0.75rem', fontWeight: 600, color: '#374151' }}>Enter Code</p>
          </div>

          {/* Arrow */}
          <div style={{ display: 'flex', alignItems: 'center', color: '#9ca3af', flexShrink: 0 }}>
            <svg style={{ width: '16px', height: '16px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </div>

          {/* THEN box */}
          <div style={{
            flex: 1,
            border: '1.5px solid #1e3a5f',
            borderRadius: '8px',
            padding: '8px 10px',
            background: '#e8edf4',
          }}>
            <p style={{ fontFamily: 'Sora, sans-serif', fontSize: '0.55rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#6b7280', marginBottom: '2px' }}>Then</p>
            <p style={{ fontFamily: 'Sora, sans-serif', fontSize: '0.75rem', fontWeight: 600, color: '#1e3a5f' }}>🏆 Access</p>
          </div>
        </div>

        {/* ── Error Message ── */}
        {error && (
          <div style={{
            background: '#fee2e2',
            border: '1px solid #fca5a5',
            borderRadius: '6px',
            padding: '8px 12px',
            marginBottom: '12px',
            textAlign: 'center',
          }}>
            <p style={{ fontFamily: 'Sora, sans-serif', fontSize: '0.8125rem', color: '#dc2626', fontWeight: 500 }}>{error}</p>
          </div>
        )}

        {/* ── 3×4 Numeric Keypad ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
          {keypadRows.flat().map((key) => {
            const isGO = key === 'GO';
            const isDEL = key === 'DEL';
            const isGoDisabled = isGO && (filledCount < 6 || isSubmitting);

            return (
              <button
                key={key}
                onClick={() => handleKeyPress(key)}
                disabled={isGoDisabled || isSubmitting}
                style={{
                  height: '48px',
                  borderRadius: '6px',
                  border: `1px solid ${isGO ? '#1e3a5f' : '#ced2d8'}`,
                  background: isGO ? '#1e3a5f' : '#f8f9fa',
                  color: isGO ? '#ffffff' : isDEL ? '#9ca3af' : '#1e3a5f',
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: isGO ? 700 : isDEL ? 500 : 600,
                  fontSize: isGO ? '0.8125rem' : '1.125rem',
                  cursor: isGoDisabled ? 'not-allowed' : 'pointer',
                  opacity: isGoDisabled ? 0.4 : 1,
                  transition: 'all 0.15s ease',
                  letterSpacing: isGO ? '0.05em' : 'normal',
                }}
                onMouseEnter={(e) => {
                  if (!isGoDisabled && !isSubmitting) {
                    (e.currentTarget as HTMLButtonElement).style.background = isGO ? '#254a78' : '#e8edf4';
                  }
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = isGO ? '#1e3a5f' : '#f8f9fa';
                }}
              >
                {isSubmitting && isGO ? '…' : key}
              </button>
            );
          })}
        </div>

        {/* ── Footnote ── */}
        <p style={{ fontFamily: 'Sora, sans-serif', fontSize: '0.6rem', color: '#9ca3af', textAlign: 'center', marginTop: '20px', letterSpacing: '0.03em' }}>
          BCBAs · BCaBAs · Lead Behavior Analyst · Student Analysts
        </p>

        {/* ── Discreet Admin Link ── */}
        <div style={{ textAlign: 'center', marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #f2f3f5' }}>
          <button
            onClick={handleAdminLinkClick}
            style={{
              fontFamily: 'Sora, sans-serif',
              fontSize: '0.6rem',
              color: '#d1d5db',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              letterSpacing: '0.04em',
              padding: '2px 4px',
              transition: 'color 0.15s ease',
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = '#9ca3af'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = '#d1d5db'; }}
          >
            admin
          </button>
        </div>
      </div>

      {/* ── Admin Password Modal ── */}
      {showAdminModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '16px',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) handleAdminModalClose(); }}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '12px',
              padding: '28px 24px',
              width: '100%',
              maxWidth: '360px',
              boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
            }}
          >
            {/* Modal Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '32px', height: '32px', borderRadius: '8px',
                  background: '#e8edf4', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg style={{ width: '16px', height: '16px', color: '#1e3a5f' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <div>
                  <p style={{ fontFamily: 'Playfair Display, serif', fontWeight: 700, fontSize: '1rem', color: '#111827', margin: 0 }}>Admin Access</p>
                  <p style={{ fontFamily: 'Sora, sans-serif', fontSize: '0.65rem', color: '#9ca3af', margin: 0 }}>Enter admin password to continue</p>
                </div>
              </div>
              <button
                onClick={handleAdminModalClose}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '4px' }}
              >
                <svg style={{ width: '18px', height: '18px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleAdminSubmit}>
              <div style={{ marginBottom: '12px' }}>
                <input
                  type="password"
                  value={adminPassword}
                  onChange={(e) => { setAdminPassword(e.target.value); setAdminError(''); }}
                  placeholder="Password"
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    border: adminError ? '1.5px solid #fca5a5' : '1.5px solid #ced2d8',
                    fontFamily: 'Sora, sans-serif',
                    fontSize: '0.875rem',
                    color: '#111827',
                    outline: 'none',
                    boxSizing: 'border-box',
                    background: '#f9fafb',
                  }}
                />
              </div>

              {adminError && (
                <div style={{
                  background: '#fee2e2',
                  border: '1px solid #fca5a5',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  marginBottom: '12px',
                }}>
                  <p style={{ fontFamily: 'Sora, sans-serif', fontSize: '0.8rem', color: '#dc2626', margin: 0 }}>{adminError}</p>
                </div>
              )}

              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  onClick={handleAdminModalClose}
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1.5px solid #ced2d8',
                    background: '#f9fafb',
                    fontFamily: 'Sora, sans-serif',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    color: '#6b7280',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={adminSubmitting || !adminPassword.trim()}
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: '8px',
                    border: 'none',
                    background: adminSubmitting || !adminPassword.trim() ? '#e8edf4' : '#1e3a5f',
                    fontFamily: 'Sora, sans-serif',
                    fontSize: '0.8125rem',
                    fontWeight: 700,
                    color: adminSubmitting || !adminPassword.trim() ? '#9ca3af' : '#ffffff',
                    cursor: adminSubmitting || !adminPassword.trim() ? 'not-allowed' : 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  {adminSubmitting ? '…' : 'Enter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
